import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  ActionGroup,
  DayPickerInput,
  Editor,
  Spacer,
  Spinner,
} from '@porsche-code/kado-ui';
import {
  PButton,
  PCheckbox,
  PHeading,
  PInlineNotification,
  PInputUrl,
} from '@porsche-design-system/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { ClientError } from 'graphql-request';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import { usePersonContext } from '@/context';
import {
  AnnouncementsQuery,
  useAnnouncementsQuery,
  useUpdateAnnouncementMutation,
} from '@/graphql';
import { useAnnouncementMessage, useNotification } from '@/hooks';

export const Route = createFileRoute('/admin/announcements/_auth/')({
  component: Announcements,
  beforeLoad: () => {
    return {
      breadcrumb: () => <Trans i18nKey="admin.announcement.title" />,
    };
  },
});

function Announcements() {
  const { t } = useTranslation(NAMESPACES);
  const { addToast } = useNotification();
  const queryClient = useQueryClient();

  const dateFormat =
    usePersonContext().state.settings?.dateFormat || 'YYYY/MM/DD';

  const {
    data: announcement,
    isPending,
    fetchStatus,
  } = useAnnouncementsQuery(undefined, {
    select: (data) => data.announcements[0],
  });

  const { mutateAsync: updateAnnouncement, error } =
    useUpdateAnnouncementMutation({
      onSuccess: (result) => {
        queryClient.setQueryData<AnnouncementsQuery>(
          useAnnouncementsQuery.getKey(),
          (cache) => ({
            ...cache,
            announcements: cache?.announcements.length
              ? cache.announcements.map((a, index) => ({
                  ...a,
                  ...(index === 0 && result.updateAnnouncement),
                }))
              : [result.updateAnnouncement],
          }),
        );
      },
    });

  interface AnnouncementFormValues {
    id?: string;
    isActive: boolean;
    message: string;
    isUserFeedbackActive: boolean;
    userFeedbackUrl: string | null;
    from?: string;
    to?: string;
  }

  const {
    handleSubmit,
    control,
    formState: { defaultValues, isSubmitting },
    setValue,
    getValues,
  } = useForm<AnnouncementFormValues>({
    values: {
      id: announcement?.id,
      isActive: !!announcement?.isActive,
      message: announcement?.message || '',
      isUserFeedbackActive: !!announcement?.isUserFeedbackActive,
      userFeedbackUrl: announcement?.userFeedbackUrl || '',
      from: announcement?.from ?? undefined,
      to: announcement?.to ?? undefined,
    },
  });

  const [isActive, isUserFeedbackActive, from] = useWatch({
    control,
    name: ['isActive', 'isUserFeedbackActive', 'from'],
  });

  const { message } = useAnnouncementMessage();

  if (isPending && fetchStatus !== 'idle') {
    return <Spinner size="2xl" />;
  }

  return (
    <>
      <PHeading aria-level={2} tag="h2" size="lg">
        {t('admin.announcement.title')}
      </PHeading>
      <Spacer mb="md" />
      {error && (
        <>
          <PInlineNotification
            heading={t('common:error')}
            description={`${
              error instanceof ClientError
                ? (error.response.errors?.[0].message ?? error.message)
                : `${error}`
            }`}
            state="error"
          />

          <Spacer mb="md" />
        </>
      )}
      {announcement?.isActive && (
        <>
          <PInlineNotification
            heading={message(announcement)}
            state="info"
            dismissButton={false}
          />

          <Spacer mb="lg" />
        </>
      )}
      <form
        onSubmit={handleSubmit(async (values) => {
          await updateAnnouncement(
            {
              input: {
                ...values,
                userFeedbackUrl:
                  values.userFeedbackUrl === '' ? null : values.userFeedbackUrl,
                from: values.from,
                to: values.to,
              },
            },
            {
              onSuccess: () => {
                addToast({
                  text: t('common:saveSuccessful'),
                  state: 'success',
                });
              },
            },
          );
        })}
      >
        <Controller
          control={control}
          name="isActive"
          render={({ field: { value, onChange } }) => (
            <PCheckbox
              label={t('admin.announcement.activate')}
              aria-label={t('admin.announcement.activate')}
              checked={!!value}
              onChange={(event) => {
                const { checked } = event.target as HTMLInputElement;
                onChange(checked);

                // Message is a mandatory field, therefore use the initial value if the field has been deleted.
                if (value && !getValues('message')) {
                  setValue('message', defaultValues?.message || '');
                }
              }}
            />
          )}
        />

        <Spacer mb="md" />

        {isActive && (
          <>
            <Controller
              control={control}
              name="message"
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Editor
                  toolbarPreset="description"
                  required
                  initialValue={value ? JSON.parse(value) : undefined}
                  label={t('admin.announcement.title')}
                  hideLabel
                  onChange={(val: unknown) => onChange(JSON.stringify(val))}
                  i18n={{
                    gdprTooltip: t('common:editor.confidentialData'),
                    boldLabel: t('common:editor.bold'),
                    underlineLabel: t('common:editor.underline'),
                    italicLabel: t('common:editor.italic'),
                    strikethroughLabel: t('common:editor.strikethrough'),
                    alignLeftLabel: t('common:editor.alignLeft'),
                    alignCenterLabel: t('common:editor.alignCenter'),
                    alignRightLabel: t('common:editor.alignRight'),
                    alignJustifyLabel: t('common:editor.alignJustify'),
                    outdentLabel: t('common:editor.outdent'),
                    indentLabel: t('common:editor.indent'),
                    insertLinkLabel: t('common:editor.insertLink'),
                    applyLinkLabel: t('common:editor.applyLink'),
                    removeLinkLabel: t('common:editor.removeLink'),
                    sanitizeLinkInstruction: t(
                      'common:editor.sanitizeLinkInstruction',
                    ),
                    bulletedListLabel: t('common:editor.bulletedList'),
                    numberedListLabel: t('common:editor.numberedList'),
                    insertTableLabel: t('common:editor.insertTable'),
                    columnLabel: t('common:editor.column'),
                    rowLabel: t('common:editor.row'),
                    undoLabel: t('common:editor.undo'),
                    redoLabel: t('common:editor.redo'),
                    fontSizeLabel: t('common:editor.fontSize'),
                    textColorLabel: t('common:editor.textColor'),
                    clearTextColorLabel: t('common:editor.clearTextColor'),
                    backgroundColorLabel: t('common:editor.backgroundColor'),
                    clearBackgroundColorLabel: t(
                      'common:editor.clearBackgroundColor',
                    ),
                    cidImagesStrippedNotification: t(
                      'common:editor.cidImagesStrippedNotification',
                    ),
                    captionPlaceholder: t('common:editor.captionPlaceholder'),
                    imageUploadHeading: t('common:editor.insertImage'),
                    dropzoneDescription: t('common:editor.description'),
                    dragDescription: t('common:editor.dragDescription'),
                    uploadButtonText: t('common:editor.uploadImage'),
                    resetButtonText: t('common:action.reset'),
                  }}
                />
              )}
            />
          </>
        )}

        <Spacer mb="md" />

        <Controller
          control={control}
          name="isUserFeedbackActive"
          render={({ field: { value, onChange } }) => (
            <PCheckbox
              label={t('admin.announcement.activateUserFeedback')}
              aria-label={t('admin.announcement.activateUserFeedback')}
              checked={!!value}
              onChange={(event) => {
                const { checked } = event.target as HTMLInputElement;
                onChange(checked);
              }}
            />
          )}
        />

        <Spacer mb="md" />

        {isUserFeedbackActive && (
          <>
            <Controller
              control={control}
              name="userFeedbackUrl"
              rules={{ required: true }}
              render={({ field }) => (
                <PInputUrl
                  name="userFeedbackUrl"
                  label={t('admin.announcement.userFeedbackUrl')}
                  aria-label={t('admin.announcement.userFeedbackUrl')}
                  value={field.value || ''}
                  required
                  onInput={(e) =>
                    field.onChange((e.target as HTMLInputElement).value)
                  }
                  onBlur={field.onBlur}
                />
              )}
            />

            <Spacer mb="md" />
          </>
        )}

        <Spacer mb="lg" />

        {(isActive || isUserFeedbackActive) && (
          <Container>
            <Controller
              control={control}
              name="from"
              render={({ field: { ref, value, onChange, ...field } }) => (
                <DayPickerInput
                  {...field}
                  inputRef={ref}
                  format={dateFormat}
                  label={t('admin.announcement.from')}
                  selected={value ? new Date(value) : undefined}
                  onSelect={(date) => {
                    onChange(date && dayjs(date).format('YYYY-MM-DD'));
                  }}
                />
              )}
            />

            <Controller
              control={control}
              name="to"
              rules={{
                min: {
                  value: from || '',
                  message: t('admin.announcement.dateShouldBeAfterFrom'),
                },
              }}
              render={({
                field: { ref, value, onChange, ...field },
                fieldState,
              }) => (
                <DayPickerInput
                  {...field}
                  inputRef={ref}
                  format={dateFormat}
                  label={t('admin.announcement.to')}
                  min={from ? new Date(from) : undefined}
                  state={fieldState.error ? 'error' : 'none'}
                  message={fieldState.error?.message}
                  selected={value ? new Date(value) : undefined}
                  onSelect={(date) => {
                    onChange(date && dayjs(date).format('YYYY-MM-DD'));
                  }}
                />
              )}
            />
          </Container>
        )}
        <Spacer mb="md" />

        <ActionGroup>
          <div />
          <PButton type="submit" variant="primary" loading={isSubmitting}>
            {t('common:action.save')}
          </PButton>
        </ActionGroup>
      </form>
    </>
  );
}

const Container = styled('div', {
  base: {
    display: 'flex',
    gap: 'sm',
  },
});
